import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  ControlsContainer,
  HeaderButton
} from '../common/styles';
import { MainContainer } from './styles';
import ResultsCount from '../common/ResultsCount';
import MyFeedbackResultsGrid from './components/MyFeedbackResultsGrid';
import MyFeedbackResultsFilters from './components/MyFeedbackResultsFilters';
import useMyFeedbackResultsStore from '../../store/myFeedbackResultsStore';
import useGridNavigation from '../../hooks/useGridNavigation';

const MyFeedbackSessionResults = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  // Zustand store
  const {
    assignments,
    isLoading,
    isLoadingMore,
    pageInfo,
    searchTerm,
    selectedStatuses,
    selectedReviewTypes,
    showControls,
    fetchAssignmentsWithReset,
    fetchAssignmentsLoadMore,
    setSearchTerm,
    toggleStatus,
    toggleReviewType,
    setShowControls,
    setIsReturningFromDetails,
    isReturningFromDetails,
    getFilteredAssignments,
    resetFilters,
  } = useMyFeedbackResultsStore();

  // Get filtered assignments once
  const filteredAssignments = getFilteredAssignments();

  // Grid navigation hook
  // IMPORTANT: Pass raw assignments for shouldLoadData checks (to avoid infinite loop when filters result in 0 items)
  // but we display filteredAssignments in the grid
  const {
    handleItemClick,
    handleGridScroll,
    showLoadingSpinner,
    shouldHideContent,
    loadMoreData,
    shouldLoadData,
  } = useGridNavigation({
    gridRef,
    items: assignments, // Use raw assignments for data loading checks
    columnCount: 3,
    rowHeight: 620,
    routes: {
      list: '/my-feedback-results',
      detail: (item) => `/feedback-sessions/assignment/${item.id}`,
      additionalRoutes: []
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    // Note: Not using onNavigateAway here because My Feedback Results navigates to
    // /feedback-sessions/assignment/... which has a different path prefix, causing
    // the hook to incorrectly detect it as "navigating away". Instead, we manually
    // reset filters in handleBack when navigating to home.
    loadMoreDataCallback: fetchAssignmentsLoadMore,
    isLoading,
    isLoadingMore,
    pageInfo,
  });

  // Fetch assignments on mount
  useEffect(() => {
    if (shouldLoadData() && !isReturningFromDetails) {
      // Reset filters when entering fresh (not returning from detail)
      resetFilters();
      fetchAssignmentsWithReset();
    }

    if (isReturningFromDetails) {
      setIsReturningFromDetails(false);
    }
  }, [shouldLoadData, isReturningFromDetails, fetchAssignmentsWithReset, setIsReturningFromDetails, resetFilters]);

  // Helper to capture scroll state before navigation
  const captureScrollState = useCallback((assignment) => {
    const gridElement = gridRef.current?._outerRef;
    if (!gridElement) {
      return null;
    }

    const currentScrollPosition = gridElement.scrollTop;
    const items = getFilteredAssignments();
    const itemIndex = items.findIndex(item => item.id === assignment.id);
    const rowIndex = itemIndex !== -1 ? Math.floor(itemIndex / 3) : -1;
    const itemTopPosition = rowIndex * 620;
    const offsetFromTop = itemTopPosition - currentScrollPosition;

    return {
      scrollPosition: currentScrollPosition,
      itemId: assignment.id,
      itemIndex,
      rowIndex,
      offsetFromTop,
      timestamp: Date.now()
    };
  }, [getFilteredAssignments]);

  const handleViewReport = useCallback((assignmentId, scrollState) => {
    navigate(`/feedback-sessions/assignment/${assignmentId}`, {
      state: {
        fromList: true,
        scrollState,
        returnPath: '/my-feedback-results'
      }
    });
  }, [navigate]);

  const handleBack = useCallback(() => {
    // Reset filters when navigating away from the section
    resetFilters();
    navigate('/');
  }, [navigate, resetFilters]);

  const hasFiltersApplied = searchTerm.length > 0 || selectedStatuses.length > 0 || selectedReviewTypes.length > 0;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>My Feedback Results</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton
            onClick={() => setShowControls(!showControls)}
            sx={{
              color: '#475569',
              borderColor: '#E2E8F0',
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              }
            }}
          >
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls}>
          <MyFeedbackResultsFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedStatuses={selectedStatuses}
            onStatusToggle={toggleStatus}
            selectedReviewTypes={selectedReviewTypes}
            onReviewTypeToggle={toggleReviewType}
          />
        </ControlsContainer>

        <MainContainer>
          <ResultsCount count={filteredAssignments.length} singular="assignment" plural="assignments" />

          <MyFeedbackResultsGrid
            assignments={filteredAssignments}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={{ ...pageInfo, totalCount: filteredAssignments.length }}
            handleItemClick={handleItemClick}
            handleGridScroll={handleGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            onViewReport={handleViewReport}
            hasFiltersApplied={hasFiltersApplied}
            captureScrollState={captureScrollState}
          />
        </MainContainer>
      </Content>
    </Container>
  );
};

export default MyFeedbackSessionResults;
